// One rule for the e-Transfer security question, shared by every screen that asks for one:
// Send Funds, PTC e-Transfer, transfer to my own account and Interac disbursement redemption.

export const SECURITY_QUESTION_MIN_LENGTH = 5;
export const SECURITY_QUESTION_MAX_LENGTH = 40;

// Translation keys for the ready-made questions. Every preset must pass the rule below in
// every locale (see securityQuestion.test.js).
export const SECURITY_QUESTION_PRESETS = [
  'security-question-preset-birth-city',
  'security-question-preset-first-pet',
  'security-question-preset-childhood-street',
  'security-question-preset-sports-team',
  'security-question-preset-first-car',
];

// What bps-go accepts (SecurityQuestionPattern in cardissuing/dal/schema/transfer_validation.go),
// which is PTC's own rule (eTransfer API v2.1.6, POST /v1/payment). VoPay documents no limit.
// bps-go also refuses two spaces in a row on the PTC path, so the rule below does too.
// The mobile app applies the same rule; change all three together.
const ALLOWED_CHARACTER = /[a-zA-Z0-9àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ \-.?!,#@$_+()/*';:~|]/;

// Phone keyboards type a curly apostrophe and some type an accent as a separate mark; PTC refuses
// both, so turn them into the straight apostrophe and single accented letter it accepts.
export const normalizeSecurityQuestion = value =>
  (value || '').normalize('NFC').replace(/[\u2018\u2019\u02BC]/g, '\'');

const alphanumeric = value => value.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

// Returns null when the question is acceptable, otherwise { id, values } describing the first
// problem found, ready for intl.formatMessage. The answer is optional; when given, a question
// that contains its own answer is refused.
export const validateSecurityQuestion = (question, answer = '') => {
  const trimmed = normalizeSecurityQuestion(question).trim();

  if (!trimmed) return { id: 'security-question-error-required' };

  const invalidCharacter = [...trimmed].find(character => !ALLOWED_CHARACTER.test(character));
  if (invalidCharacter) {
    return {
      id: 'security-question-error-character',
      values: { character: invalidCharacter },
    };
  }

  if (/\s{2,}/.test(trimmed)) return { id: 'security-question-error-spaces' };

  if (
    trimmed.length < SECURITY_QUESTION_MIN_LENGTH ||
    trimmed.length > SECURITY_QUESTION_MAX_LENGTH
  ) {
    return {
      id: 'security-question-error-length',
      values: { min: SECURITY_QUESTION_MIN_LENGTH, max: SECURITY_QUESTION_MAX_LENGTH },
    };
  }

  const normalizedAnswer = alphanumeric(answer || '');
  if (normalizedAnswer.length >= 3 && alphanumeric(trimmed).includes(normalizedAnswer)) {
    return { id: 'security-question-error-contains-answer' };
  }

  return null;
};

// Convenience for screens that show the message as a plain string.
export const securityQuestionErrorMessage = (intl, error) =>
  error ? intl.formatMessage({ id: error.id }, error.values) : '';
